import { useEffect } from 'react';
import api from '../services/api';

/**
 * Política de sesión de la organización (`policies.sessionTimeout`).
 *
 * El servidor es la autoridad: corta la sesión por inactividad y devuelve 401.
 * Este hook solo hace que el panel lo refleje sin esperar a la siguiente
 * petición, y evita que alguien que está trabajando (leyendo la bóveda, que va
 * en caché, o escribiendo) sea desconectado por no haber lanzado peticiones:
 *   - cuenta la actividad del usuario (puntero, teclado, rueda, táctil);
 *   - si pasa el timeout efectivo sin actividad, cierra sesión en local;
 *   - si hay actividad, cada timeout/3 refresca la sesión en el servidor con
 *     GET /cards/info/current, que además devuelve el timeout efectivo vigente
 *     (por si el admin lo cambió mientras tanto).
 * Solo actúa cuando el servidor ha dicho que el límite viene de la organización
 * (`sessionLimitedByOrg` en localStorage); sin política, no hace nada.
 */
const TIMEOUT_MS = {
  '5m': 5 * 60e3, '15m': 15 * 60e3, '30m': 30 * 60e3, '1h': 60 * 60e3,
  '2h': 2 * 60 * 60e3, '4h': 4 * 60 * 60e3, '8h': 8 * 60 * 60e3,
  '24h': 24 * 60 * 60e3, browser: 24 * 60 * 60e3,
};
const TICK_MS = 15000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];

// `cfg` = sessionConfig de login/verify o effectiveSessionConfig de info/current.
export function applyEffectiveSessionConfig(cfg) {
  if (!cfg || !cfg.timeout) return;
  localStorage.setItem('sessionTimeout', cfg.timeout);
  localStorage.setItem('sessionLimitedByOrg', cfg.limitedByOrg ? '1' : '0');
}

export default function useOrgSessionLimit(isAuthenticated, onExpired) {
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let lastActivity = Date.now();
    let lastRefresh = Date.now();
    let activeSinceRefresh = false;
    let refreshing = false;

    const onActivity = () => { lastActivity = Date.now(); activeSinceRefresh = true; };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const tick = setInterval(async () => {
      if (localStorage.getItem('sessionLimitedByOrg') !== '1') return;
      const limit = TIMEOUT_MS[localStorage.getItem('sessionTimeout')];
      if (!limit) return;

      const now = Date.now();
      if (now - lastActivity > limit) { onExpired(); return; }

      if (activeSinceRefresh && !refreshing && now - lastRefresh > limit / 3) {
        refreshing = true;
        activeSinceRefresh = false;
        lastRefresh = now;
        try {
          const res = await api.get('/cards/info/current');
          applyEffectiveSessionConfig(res.data?.effectiveSessionConfig);
        } catch (_) { /* un 401 ya lo gestiona el interceptor global; la red caída se reintenta */ }
        refreshing = false;
      }
    }, TICK_MS);

    return () => {
      clearInterval(tick);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [isAuthenticated, onExpired]);
}
